import { computed, inject, Injectable, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Firestore, doc, getDoc, DocumentSnapshot } from '@angular/fire/firestore';
import { switchMap, of } from 'rxjs';
import { EspacioService } from './espacio.service';
import { ReservaService } from './reserva.service';
import { CuentaService } from './cuenta.service';
import { PagosService } from './pagos.service';
import { ResidenteQrService } from './residente-qr.service';
import { Espacio } from '../models/espacio.model';
import { Reserva } from '../models/reserva.model';
import { Cuenta } from '../models/cuenta.model';
import { Pago } from '../models/pago.model';
import { Qr } from '../models/qr.model';

const LS_KEY = 'rc_residente_state';

export interface ResidenteSession {
  uid:                 string;
  nombre:              string;
  email:               string;
  residencialId:       string;
  nombreResidencial:   string;
}

/**
 * Estado de sesión del residente.
 *
 * Mantiene el perfil mínimo de identidad (uid, residencialId, etc.) y los listeners
 * de datos del residente (espacios, reservas, pagos, QRs). Listener único por colección,
 * vivo toda la sesión — no se recrea al navegar entre páginas.
 */
@Injectable({ providedIn: 'root' })
export class ResidenteStateService {
  private firestore  = inject(Firestore);
  private espacioSvc = inject(EspacioService);
  private reservaSvc = inject(ReservaService);
  private cuentaSvc  = inject(CuentaService);
  private pagosSvc   = inject(PagosService);
  private qrSvc      = inject(ResidenteQrService);

  private _session = signal<ResidenteSession | null>(null);

  readonly session = this._session.asReadonly();

  private readonly _residencialId  = computed(() => this._session()?.residencialId ?? '');
  private readonly _residencialId$ = toObservable(this._residencialId);

  private readonly _sessionKey = computed(() => ({
    residencialId: this._session()?.residencialId ?? '',
    uid:           this._session()?.uid           ?? '',
  }));
  private readonly _sessionKey$ = toObservable(this._sessionKey);

  /**
   * Espacios del residencial del residente logueado.
   * undefined = aún no ha llegado la primera respuesta de Firestore (loading).
   */
  readonly espacios = toSignal(
    this._residencialId$.pipe(
      switchMap(id => id ? this.espacioSvc.getAll(id) : of([] as Espacio[]))
    )
  );

  /**
   * Reservas activas del residente logueado, ordenadas por fechaInicio desc.
   * undefined = cargando (primera respuesta aún no llegó).
   */
  readonly cuentas = toSignal(
    this._residencialId$.pipe(
      switchMap(id => id ? this.cuentaSvc.getAll(id) : of([] as Cuenta[]))
    )
  );

  readonly misReservas = toSignal(
    this._sessionKey$.pipe(
      switchMap(({ residencialId, uid }) =>
        residencialId && uid
          ? this.reservaSvc.getReservasByResidente(residencialId, uid)
          : of([] as Reserva[])
      )
    )
  );

  readonly misPagos = toSignal(
    this._sessionKey$.pipe(
      switchMap(({ residencialId, uid }) =>
        residencialId && uid
          ? this.pagosSvc.getPagosPorResidente(residencialId, uid)
          : of([] as Pago[])
      )
    )
  );

  readonly misQrs = toSignal(
    this._sessionKey$.pipe(
      switchMap(({ residencialId, uid }) =>
        residencialId && uid
          ? this.qrSvc.getMisQrs(residencialId, uid)
          : of([] as Qr[])
      )
    )
  );

  constructor() {
    this.hydrateFromStorage();
  }

  /**
   * Lee usuarios/{uid} para obtener residencialId, luego residenciales/{id} para
   * el nombre de la residencial. Solo persiste los campos mínimos de identidad.
   * Debe llamarse en AuthService.login() antes de navegar al módulo /residente.
   */
  async loadForResidente(uid: string, userSnap: DocumentSnapshot): Promise<void> {
    if (!userSnap.exists()) throw new Error('Usuario residente no encontrado en Firestore');

    const userData = userSnap.data() as {
      nombre:        string;
      email:         string;
      residencialId: string;
    };

    // Solo se necesita el nombre de la residencial para mostrarlo en la UI;
    // los datos completos de la residencial no son necesarios para el residente.
    const resSnap = await getDoc(doc(this.firestore, `residenciales/${userData.residencialId}`));
    if (!resSnap.exists()) throw new Error('Residencial no encontrada en Firestore');

    const nombreResidencial = (resSnap.data() as { nombre: string }).nombre;

    const session: ResidenteSession = {
      uid,
      nombre:            userData.nombre,
      email:             userData.email,
      residencialId:     userData.residencialId,
      nombreResidencial
    };

    this._session.set(session);
    // No hay Timestamps — JSON.stringify es suficiente sin serialización especial.
    localStorage.setItem(LS_KEY, JSON.stringify(session));
  }

  /**
   * Limpia estado en memoria y localStorage.
   * Debe llamarse en AuthService.logout() antes de signOut().
   */
  clearState(): void {
    this._session.set(null);
    localStorage.removeItem(LS_KEY);
  }

  private hydrateFromStorage(): void {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return;
      this._session.set(JSON.parse(raw) as ResidenteSession);
    } catch {
      localStorage.removeItem(LS_KEY);
    }
  }
}
